import React from "react";
import { SITE } from "../lib/site";
import { Mail, MapPin, Phone, MessageCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [copied, setCopied] = React.useState(false);

  const mailto = `mailto:${SITE.email}?subject=Enquiry%20from%20website`;

  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(SITE.officePhone);
      setCopied(true);
      toast.success("Phone number copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 sm:py-32 bg-[#FAF5F0]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#064E3B]">
              Get In Touch
            </div>
            <div className="pill-divider mt-4" />
            <h2 className="font-serif-display text-3xl sm:text-5xl mt-6 leading-[1.1] text-[#111827] font-semibold">
              We're listening, by{" "}
              <span className="italic text-[#064E3B]">phone</span> or in{" "}
              <span className="italic text-[#DC2626]">person.</span>
            </h2>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-[#374151] leading-relaxed">
              Whether you want to give, volunteer, partner or recommend a family in
              need — please call, message or visit either of our offices.
            </p>

            <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
              <a
                href={`tel:${SITE.officePhone}`}
                data-testid="contact-call-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[#064E3B] hover:bg-[#053a2c] text-white px-6 py-3 text-sm font-semibold transition-all hover:scale-[1.02]"
              >
                <Phone size={16} />
                Call now
              </a>
              <a
                href={SITE.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white px-6 py-3 text-sm font-semibold transition-all hover:scale-[1.02]"
              >
                <MessageCircle size={16} />
                WhatsApp
              </a>
              <button
                onClick={copyPhone}
                data-testid="contact-copy-phone"
                className="inline-flex items-center gap-2 rounded-full border border-[#E5E0DA] bg-white hover:border-[#064E3B] text-[#111827] px-5 py-3 text-sm font-semibold transition-colors"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? "Copied" : "Copy number"}
              </button>
            </div>
          </div>

          {/* Right info cards */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            <InfoCard
              Icon={Phone}
              title="Office Phone"
              accent="#064E3B"
              testid="contact-info-phone"
            >
              <a
                href={`tel:${SITE.officePhone}`}
                data-testid="contact-phone-link"
                className="font-serif-display text-xl text-[#111827] hover:text-[#064E3B] transition-colors"
              >
                {SITE.officePhone}
              </a>
            </InfoCard>

            <InfoCard
              Icon={MessageCircle}
              title="WhatsApp"
              accent="#25D366"
              testid="contact-info-whatsapp"
            >
              <a
                href={SITE.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="font-serif-display text-xl text-[#111827] hover:text-[#25D366] transition-colors"
              >
                {SITE.whatsapp}
              </a>
            </InfoCard>

            <InfoCard
              Icon={Mail}
              title="Email"
              accent="#DC2626"
              testid="contact-info-email"
            >
              <a
                href={mailto}
                data-testid="contact-email-link"
                className="font-serif-display text-lg sm:text-xl text-[#111827] hover:text-[#DC2626] transition-colors break-all"
              >
                {SITE.email}
              </a>
            </InfoCard>

            <InfoCard
              Icon={MapPin}
              title="Account Name"
              accent="#D4AF37"
              testid="contact-account-name"
            >
              <p className="font-serif-display text-base sm:text-lg text-[#064E3B] leading-snug">
                {SITE.accountName}
              </p>
            </InfoCard>

            <InfoCard
              Icon={MapPin}
              title="Kogi State Office"
              accent="#064E3B"
              testid="contact-office-kogi"
            >
              <p className="text-[#374151] leading-relaxed text-sm">
                {SITE.offices[0]}
              </p>
            </InfoCard>

            <InfoCard
              Icon={MapPin}
              title="Abuja Office"
              accent="#DC2626"
              testid="contact-office-abuja"
            >
              <p className="text-[#374151] leading-relaxed text-sm">
                {SITE.offices[1]}
              </p>
            </InfoCard>

            {/* Bank reminder */}
            <div className="sm:col-span-2 rounded-3xl bg-[#064E3B] text-white p-7 sm:p-8 relative overflow-hidden">
              <div className="absolute inset-0 geo-overlay-light opacity-40" />
              <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FCD34D]">
                    Ready to give?
                  </div>
                  <div className="mt-2 font-serif-display text-xl sm:text-2xl">
                    See bank details above
                  </div>
                </div>
                <a
                  href="#donate"
                  data-testid="contact-go-donate"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white px-6 py-3 text-sm font-semibold transition-all hover:scale-[1.02]"
                >
                  Donate now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoCard({ Icon, title, accent, testid, children }) {
  return (
    <div
      data-testid={testid}
      className="rounded-3xl border border-[#E5E0DA] bg-white p-6 sm:p-7 transition-all hover:-translate-y-1 hover:shadow-lg"
    >
      <div
        className="inline-flex items-center justify-center h-11 w-11 rounded-2xl mb-4 sm:mb-5"
        style={{ backgroundColor: `${accent}15`, color: accent }}
      >
        <Icon size={20} strokeWidth={1.75} />
      </div>
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#064E3B] mb-2">
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}
